/**
 * Copyright 2019 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import root from 'window-or-global';
import type { ThunkAction, Action, Dispatch } from '../../types';
import fetchMiniCartData from '../../actions/miniCart';
import actionTypes from '../../types/action';
import { eventTypes } from '../../constants/event';
import { cartConstants } from '../../constants/cart';
import { getFulfillmentOptionsByGroup } from '@/utils/itemHelpers/fulfillmentHelpers/getFulfillmentOptionsByGroup';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { type useAddToCart } from '@/features/orders/api/postAddToCart';
import type { QueryClient } from '@tanstack/react-query';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { type ProductPriceInfoAddToCartAnalyticsData } from '@/features/shelf';
import { type ShippingType } from '@/features/fulfillment';
import { type AxiosError } from 'axios';

const trackAddToCartShelf = (
  addOptions: AddToCartParams,
  analyticsData: ProductPriceInfoAddToCartAnalyticsData,
  isBopus: boolean,
  productAvailableQuantity?: number,
  fulfillmentTypeid?: number,
  acesEnabled?: boolean,
  acesYmme?: string[],
) => {
  const fulfillmentTypeId = [fulfillmentTypeid];
  const { pageName = '', pageType = '' } = root.utag_data || {};
  const data: Record<string, string | number | undefined | Array<string | number | undefined>> = {
    eventType: 'addToCart',
    productPriceList: [analyticsData.price],
    productRequestedQuantityList: [addOptions.quantity],
    productQuantityList: [addOptions.quantity],
    productBrandList: [analyticsData.brandName],
    productIdList: [addOptions.sku_id],
    productFulfillmentTypeList: fulfillmentTypeId,
    productRank: !isNaN(analyticsData.prodIndex as number)
      ? Number(analyticsData.prodIndex) + 1
      : analyticsData.prodIndex,
    productPartNumberList: [analyticsData.partNumber ?? ''],
    productPartList: [analyticsData.name],
    customLinkName: analyticsData.customLinkName,
    productRecommendationTypeList: analyticsData.productRecommendationTypeList,
    pageName: pageName,
    pageType: pageType,
    productStrikeThroughPricing: analyticsData.productStrikeThroughPricing,
    productFulfillmentOptionSeenByCustomer: analyticsData.productFulfillmentOptionSeenByCustomer,
  };

  if (productAvailableQuantity !== undefined) {
    data.productAvailableQuantityList = [productAvailableQuantity];
  }

  if (analyticsData.corePrice !== 0) {
    data.productCorePriceList = [analyticsData.corePrice];
  }

  if (acesEnabled) {
    data.partGroupId = [analyticsData.partGroupId];
    data.partTerminologyId = [analyticsData.partTerminologyId];
    if (acesYmme && acesYmme.length) {
      data.aces_ymme = acesYmme;
    }
  } else {
    data.originalPartTypeIdList = [analyticsData.originalPartTypeId];
    data.productRepositoryIdList = analyticsData.productRepositoryIdList;
  }

  clickTrack(data);
};

type AddToCartParams = {
  sku_id: string;
  product_id?: string;
  quantity: string;
  category_id: string;
  order_type: ShippingType;
  fulfillmentId?: number;
  catalog_vehicle_id?: string;
  store_number: string;
  storePickupAvailable?: boolean;
  xFusionQueryId?: string;
  positionId?: string;
  brandId?: string;
  uniqueId?: string;
};

const {
  ADD_TO_CART_SHELF_FAILURE,
  ADD_TO_CART_SHELF_REQUESTING,
  ADD_TO_CART_SUCCESS,
  EMPTY_CART_ITEMS,
  EMPTY_DATA,
  SET_SELECTED_NULL_CARTRIDGE,
  SET_LOADING_FOR_PRODUCT,
  SET_PRODUCT_FILTER_EVENTS,
  CLEAR_PRODUCT_FILTER_EVENTS,
  CLEAR_PRODUCT_FILTER_SELECTION,
  SET_FILTER_SCROLL,
  SET_FILTER_SCROLL_BRAND,
  SET_ITEMS_PER_PAGE_OR_SORT_BY_FOCUS,
} = actionTypes;

const setLoadingForProduct = (skuNumber?: number): Action => ({
  type: SET_LOADING_FOR_PRODUCT,
  skuNumber,
});

export const addToCart =
  (
    addToCartMutation: ReturnType<typeof useAddToCart>,
    addOptionsParms: AddToCartParams,
    analyticsData: ProductPriceInfoAddToCartAnalyticsData | null,
    onSuccess: () => void,
    queryClient: QueryClient,
    searchedTerm?: string,
    xFusionQueryId?: string,
    acesEnabled?: boolean,
    acesYmme?: string[],
  ): ThunkAction =>
  async (dispatch) => {
    // @ts-expect-error force cast from Dispatch<Action> to Dispatch<ThunkAction>
    const dispatchThunk = dispatch as Dispatch<ThunkAction>;

    try {
      const { storePickupAvailable, ...addOptions } = addOptionsParms;
      const isBopus = addOptions.order_type === cartConstants.STOREORDER && storePickupAvailable!;
      const orderType =
        typeof addOptions.order_type === 'number'
          ? addOptions.order_type
          : addOptions.order_type === cartConstants.ONLINEORDER
            ? FULFILLMENT_METHODS.ONLINEORDER
            : FULFILLMENT_METHODS.STOREORDER;
      const fulfillmentTypeid = addOptions.fulfillmentId;
      dispatch(setLoadingForProduct(Number(addOptions.sku_id)));
      dispatch({ type: ADD_TO_CART_SHELF_REQUESTING });
      // format request type for v2 fulfillment
      const orderV2Request = {
        skuId: addOptions.sku_id,
        productId: addOptions.product_id,
        quantity: addOptions.quantity,
        fulfillmentTypeId: fulfillmentTypeid ?? orderType,
        vehicleId: addOptions.catalog_vehicle_id,
        storeNumber: addOptions.store_number,
        searchedTerm,
        xFusionQueryId,
        positionId: addOptions.positionId,
        brandId: addOptions.brandId,
        uniqueId: addOptions.uniqueId,
      };
      const data = await addToCartMutation.mutateAsync(orderV2Request);
      onSuccess?.();
      const availabilityInfo = getFulfillmentOptionsByGroup(
        data.lineItemFulfillmentOptions.find((items) => items.skuId === addOptions.sku_id)
          ?.fulfillmentOptions,
      );
      dispatch({
        type: ADD_TO_CART_SUCCESS,
        data: {
          ...data,
          availabilityInfo,
          orderType: addOptions.order_type,
          quantity: addOptions.quantity,
          skuId: addOptions.sku_id,
          productId: addOptions.product_id!,
        },
      });

      const productAvailableQuantity =
        availabilityInfo.find((option) => option.selected)?.maxAvailableQty ?? 0;
      if (analyticsData) {
        trackAddToCartShelf(
          addOptions,
          analyticsData,
          isBopus,
          productAvailableQuantity,
          fulfillmentTypeid,
          acesEnabled,
          acesYmme,
        );
      }

      dispatchThunk(fetchMiniCartData(queryClient));
    } catch (err: unknown) {
      const error = err as AxiosError<{
        title: string;
        errorKey: string;
        errorMessageDefault: string;
      }>;
      dispatch({
        type: ADD_TO_CART_SHELF_FAILURE,
        err: error.response?.data.title,
        errorKey: error?.response?.data.errorKey,
        errorMessageDefault: error.response?.data?.errorMessageDefault,
        errorSku: addOptionsParms.sku_id,
        errorLog: err,
        quantity: addOptionsParms.quantity,
        orderType: addOptionsParms.order_type,
      });
    } finally {
      dispatch(setLoadingForProduct());
    }
  };

export const removeDataPart = (): Action => ({ type: EMPTY_DATA });

export const removeDataFromPage = (): Action => ({ type: EMPTY_CART_ITEMS });

export const setSelectedNullCartridge = (value: string): Action => ({
  type: SET_SELECTED_NULL_CARTRIDGE,
  value,
});

export const setProductFilterEvents = (
  filterName: string,
  filterId: string,
  checked: boolean,
  quickFilter?: boolean,
): Action => {
  const eventType = checked ? eventTypes.applyFilter : eventTypes.removeFilter;
  const selectedFilterValue =
    quickFilter && !filterName.includes('Quick Filter')
      ? `${eventTypes.quickFilterEvent}: ${filterName}`
      : filterName;

  return {
    type: SET_PRODUCT_FILTER_EVENTS,
    filterScroll: quickFilter,
    selectedFilterValue,
    filterId,
    eventType,
    checked,
  };
};

export const clearProductFilterEvents = (): Action => {
  return {
    type: CLEAR_PRODUCT_FILTER_EVENTS,
  };
};

export const clearProductFilterSelection = (): Action => {
  return {
    type: CLEAR_PRODUCT_FILTER_SELECTION,
  };
};

export const setFilterScroll = (value: boolean): Action => {
  return { type: SET_FILTER_SCROLL, value };
};

export const setFilterScrollBrand = (value: boolean): Action => {
  return { type: SET_FILTER_SCROLL_BRAND, value };
};

export const setSortByOrItemsPerPageFocus = (
  focusSortBy: boolean,
  focusItemsPerPage: boolean,
): Action => {
  return { type: SET_ITEMS_PER_PAGE_OR_SORT_BY_FOCUS, focusSortBy, focusItemsPerPage };
};
